// LAP-765-CARD — Agrégation des sources Life du conseiller temps.
//
// Extrait la logique multi-endpoints utilisée par `ConseillerTempsPage`
// (fetchAllSources) pour la rendre réutilisable depuis la carte « Conseils
// et Astuces » du dashboard Temps, qui doit calculer le VRAI score temps via
// `buildTimeAdvisor` sans dupliquer la liste d'endpoints.
//
// Chaque requête est isolée (catch indépendant) : un endpoint absent ne doit
// pas faire tomber tout l'agrégat — `buildTimeAdvisor` produit alors un
// diagnostic partiel mais exploitable.
import { makeGetAllRequest, makeGetRequest } from 'utils/api'
import urls from 'utils/urls'

/**
 * Normalise une réponse API en tableau, quelle que soit l'enveloppe
 * (array nu, { results: [...] }, { data: [...] }).
 */
export function extractList(payload) {
  if (!payload) return []
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload.results)) return payload.results
  if (Array.isArray(payload.data)) return payload.data
  return []
}

/**
 * Récupère toutes les sources nécessaires à `buildTimeAdvisor`.
 *
 * @param {object} fallbackUser - profil Redux à utiliser si /api/user/user/
 *   échoue (évite un agrégat sans utilisateur).
 * @returns {Promise<object>} agrégat prêt à passer à buildTimeAdvisor.
 */
export async function fetchTimeAdvisorSources(fallbackUser = {}) {
  const safeGet = async (path) => {
    try {
      const r = await makeGetRequest(path, {}, true)
      return r?.data ?? null
    } catch (_) {
      return null
    }
  }

  // Variante « collection complète » : les listes DRF sont paginées à 20
  // (LimitOffsetPagination globale). Un simple makeGetRequest tronquait donc
  // les événements aux 20 PLUS ANCIENS (tri start_date ASC) — les séances de
  // sport récentes du calendrier n'arrivaient jamais jusqu'à l'advisor.
  const safeGetAll = async (path) => {
    try {
      const r = await makeGetAllRequest(path, {}, true)
      return r?.data ?? null
    } catch (_) {
      return null
    }
  }

  const [
    userData,
    events,
    tasks,
    projects,
    objectifs,
    roadmapIdeas,
    sportSessions,
    wheelOfLife,
    timeProfile,
    experiences,
    categories,
  ] = await Promise.all([
    safeGet('/api/user/user/'),
    // Événements BRUTS (1 ligne / événement) : le dépliage des récurrences
    // est fait par buildTimeAdvisor (expandOccurrences). Cf. ConseillerTempsPage.
    safeGetAll('/api/time/events/'),
    safeGetAll(urls.fetchTasks),
    safeGetAll(urls.projectOperations),
    safeGetAll(urls.crudMesObjectifs),
    safeGetAll(urls.roadmapIdeas),
    safeGet('/api/health/sport-sessions/'),
    safeGet('/api/general/wheel-of-life/'),
    safeGet('/api/general/time-profile/'),
    safeGetAll(`${urls.crudParcoursProOperations}?ParcoursType=Expérience`),
    // Référentiel Time.Category (non paginé) : les événements bruts portent
    // `category_id` en ENTIER ; sans ce référentiel, buildTimeAdvisor ne peut
    // pas résoudre le nom ("Sport", …) et tout tombe en « Sans catégorie ».
    safeGet('/api/time/get-category-dd'),
  ])

  return {
    user: userData || fallbackUser || {},
    events: extractList(events),
    categories: extractList(categories),
    tasks: extractList(tasks),
    projects: extractList(projects),
    objectifs: extractList(objectifs),
    roadmapIdeas: extractList(roadmapIdeas),
    sportSessions: extractList(sportSessions),
    wheelOfLife: wheelOfLife || {},
    timeProfile: timeProfile || {},
    experiences: extractList(experiences),
  }
}
